import { useAuthContext } from "../../auth/state/AuthContext";
import { getCompletedBalticForms, getPendingBalticForms } from "../../baltic-form/services/baltic-forms.service";
import NotificationCard from "../../notifications/components/NotificationCard";
import { NotificationsHeader } from "../../notifications/components/NotificationsHeaderMarkAsRead";
import { NotificationM } from "../../notifications/models/notifications.models";
import { getUserNotifications, postNotificationRead } from "../../notifications/services/notifications.service";
import HeaderTechform from "../../shared/app-shell/HeaderTechform";
import PageFooter from "../../shared/app-shell/PageFooter";
import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { ToastContainer } from "../../shared/components/ToastContainer";
import { log, logE, logV } from "../../shared/services/debug-logger";
import { ContactSupport } from "../components/ContactSupport";
import { TechFormRequestsListHeader } from "../components/TechFormRequestsListHeader";
import { TechFormsCompleted } from "../components/TechFormsCompleted";
import { TechFormsPending } from "../components/TechFormsPending";
import { Tab, Tabs } from "../components/TechformTabs";
import ZeroNotifications from "../components/ZeroNotifications";
import { TechForm } from "../models/tech-form.model";
import { getCompletedTechForms, getPendingTechForms, openPendingForm } from "../services/tech-forms.service";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const LandingPageTF: React.FC = () => {
  const theme = useAtomValue(themeAtom);

  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const [pendingForms, setPendingForms] = useState<TechForm[]>([]);
  const [completedForms, setCompletedForms] = useState<{ tech?: TechForm[]; baltic?: TechForm[] }>({});
  const [notifications, setNotifications] = useState<NotificationM[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const stepnoString = localStorage.getItem("techFormsetpCount");
  const stepno = stepnoString ? parseInt(stepnoString, 10) : 0;
  const storedForm = localStorage.getItem("vesselDetailsForm") ?? "null";
  const parsedFormData = JSON.parse(storedForm);
  const lsVesselName = parsedFormData?.vesselName ?? "null";
  const lsImoNumber = parsedFormData?.imoNumber ?? "null";
  const navigate = useNavigate();
  logV("LandingPageTF - BUILD", { loading: isLoading, pending: pendingForms.length });

  useEffect(() => {
    const fetchFormsAndNotifications = async () => {
      log("LandingPageTF - fetchFormsAndNotifications");
      setIsLoading(true);

      try {
        // Pending forms
        const pendingResults = await Promise.allSettled([getPendingTechForms(), getPendingBalticForms()]);

        const pendingTech = pendingResults[0].status === "fulfilled" ? pendingResults[0].value : [];
        const pendingBaltic = pendingResults[1].status === "fulfilled" ? pendingResults[1].value : [];
        setPendingForms([...pendingTech, ...pendingBaltic]);
        const pendingFailures = pendingResults.flatMap((result, index) =>
          result.status === "rejected" ? [index === 0 ? "TECH" : "BALTIC"] : [],
        );
        log("STATE - fetchFormsAndNotifications - pending applied", { count: pendingTech.length + pendingBaltic.length, failedSources: pendingFailures.join(",") || "none" });

        // Completed forms
        const completedResults = await Promise.allSettled([getCompletedTechForms(), getCompletedBalticForms()]);

        const completedTech = completedResults[0].status === "fulfilled" ? completedResults[0].value : [];
        const completedBaltic = completedResults[1].status === "fulfilled" ? completedResults[1].value : [];
        setCompletedForms({ tech: completedTech, baltic: completedBaltic });
        const completedFailures = completedResults.flatMap((result, index) =>
          result.status === "rejected" ? [index === 0 ? "TECH" : "BALTIC"] : [],
        );
        log("STATE - fetchFormsAndNotifications - completed applied", { count: completedTech.length + completedBaltic.length, failedSources: completedFailures.join(",") || "none" });

        // Notifications
        try {
          const notifications = await getUserNotifications();
          setNotifications(notifications);
          log("STATE - fetchFormsAndNotifications - notifications applied", { count: notifications.length });
        } catch (notifError) {
          logE(notifError, { operation: "LandingPageTF - fetchFormsAndNotifications - notifications failed" });
        }
      } catch (error) {
        logE(error, { operation: "LandingPageTF - fetchFormsAndNotifications - failed" });
      } finally {
        log("LandingPageTF - fetchFormsAndNotifications - finished");
        setIsLoading(false);
        sessionStorage.clear(); // TODO: refine this to not nuke unrelated session state
      }
    };

    fetchFormsAndNotifications();
  }, [email]);

  const openCompletedFormAndMarkNotifAsRead = (techForm: TechForm) => {
    log("LandingPageTF - openCompletedFormAndMarkNotifAsRead", { type: techForm.TYPE, imoNumber: techForm.IMO_NUMBER });
    sessionStorage.setItem("techFormsetpCount", "1");
    const { IMO_NUMBER: imo_no, VESSEL_NAME: vessel_name } = techForm;

    const associatedNotification = notifications.find(
      (notification) =>
        "IMO_NUMBER" in notification &&
        notification.IMO_NUMBER === techForm.IMO_NUMBER &&
        notification.VESSEL_NAME === techForm.VESSEL_NAME,
    );

    if (associatedNotification && !associatedNotification.IS_READ) {
      log("LandingPageTF - openCompletedFormAndMarkNotifAsRead - mark read", { notificationId: associatedNotification.ID });
      postNotificationRead(associatedNotification.ID);
    }

    if (techForm.TYPE === "TECH") {
      navigate(`/technicalForm/completed/${imo_no}/${vessel_name}/vessel-detail`);
    } else {
      navigate(`/balticForm/completed/${imo_no}/${vessel_name}/general-info`);
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div className={twMerge("flex h-full flex-col justify-between px-6 pt-6", theme.home.tailwind.pageBackground)}>
        <div>
          <HeaderTechform />
          <h1 className={twMerge("mb-4 text-4xl font-bold", theme.home.tailwind.textDefault)}>Dashboard</h1>
          <div className={theme.home.tailwind.textDefault}>
            Monitor all your requests and documentation in one place. Easily track what needs your attention and what's
            already completed.
          </div>

          {/* Form Requests */}
          <div className="mt-10 flex gap-6">
            <div className={twMerge("h-full w-3/5 rounded-lg p-6", theme.certVesselSelect.tableBg)}>
              <TechFormRequestsListHeader />
              <div className="mt-4">
                <Tabs>
                  {/* Pending */}
                  <Tab label="Pending">
                    <TechFormsPending
                      forms={pendingForms}
                      lsVesselName={lsVesselName}
                      lsImoNumber={lsImoNumber}
                      stepno={stepno}
                      openForm={openPendingForm}
                    />
                  </Tab>

                  {/* Completed */}
                  <Tab label="Completed">
                    <TechFormsCompleted
                      forms={completedForms}
                      openCompletedTechForm={openCompletedFormAndMarkNotifAsRead}
                    />
                  </Tab>
                </Tabs>
              </div>
            </div>

            {/* Notifications */}
            <div className="flex w-2/5 flex-col gap-6">
              <div
                className={twMerge("flex flex-1 flex-col gap-6 rounded-lg pb-4 pt-6", theme.certVesselSelect.tableBg)}
              >
                <NotificationsHeader notifications={notifications} />

                {/* Divider */}
                <div className={twMerge("block w-full border-b", theme.myVesselsTable.tailwind.tableBorder)} />
                <div className="overflow-y-auto px-6" style={{ maxHeight: "400px" }}>
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <NotificationCard key={notification.ID} notification={notification} />
                    ))
                  ) : (
                    <ZeroNotifications />
                  )}
                </div>
              </div>

              <ContactSupport />
            </div>
          </div>
        </div>

        <PageFooter />
        <ToastContainer />
      </div>
    </LoadingWrapper>
  );
};

export default LandingPageTF;
